import { CountryRegionField, StateProvinceField } from '@/components/address-fields';
import { Stack } from '@/components/layout';
import { FormInput } from '@/components/ui/form/form-input';
import { STRINGS } from '@/constants/strings';
import { MutatePlayerForm } from '@/features/player/components/mutate-player/types';
import { useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import {
  MAX_CITY_LENGTH,
  MAX_EMAIL_LENGTH,
  MAX_NAME_LENGTH,
  MAX_PHONE_LENGTH,
  MAX_ZIP_LENGTH,
} from '@/validation';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

export const MutatePlayerContactSectionTablet = () => {
  const { scale, isTablet } = useScaling();
  const rowGap = scale(isTablet ? 12 : 18);
  const { control, setValue } = useFormContext<MutatePlayerForm>();
  const country = useWatch({ control, name: 'country' });
  const addressFieldProps = {
    uppercaseLabel: true,
    labelVariant: 'Font Inter/Subtitle/Regular 11px' as const,
    triggerVariant: 'Font Inter/Subtitle/Regular 14px' as const,
    inputVariant: 'Font Inter/Subtitle/Regular 14px' as const,
    labelGap: 8,
    labelStyle: { color: colors['Labels/Primary'] },
    triggerStyle: {
      height: scale(isTablet ? 48 : 44),
      minHeight: scale(isTablet ? 48 : 44),
      borderRadius: scale(4),
      borderColor: colors['5'],
      backgroundColor: colors['Backgrounds/Primary'],
    },
  };

  return (
    <>
      <Stack direction="row" gap={rowGap} width="100%">
        <Stack flex={1} minWidth={0}>
          <Controller
            name="parentFirstName"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <FormInput
                label={STRINGS.player.mutatePlayer.contact.parentFirstName}
                value={value}
                required
                trim
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                maxLength={MAX_NAME_LENGTH}
                onBlur={onBlur}
                onChangeText={parentFirstName => onChange(parentFirstName)}
                placeholder={STRINGS.player.mutatePlayer.contact.parentFirstName}
              />
            )}
          />
        </Stack>
        <Stack flex={1} minWidth={0}>
          <Controller
            name="parentLastName"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <FormInput
                label={STRINGS.player.mutatePlayer.contact.parentLastName}
                value={value}
                required
                trim
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                maxLength={MAX_NAME_LENGTH}
                onBlur={onBlur}
                onChangeText={parentLastName => onChange(parentLastName)}
                placeholder={STRINGS.player.mutatePlayer.contact.parentLastName}
              />
            )}
          />
        </Stack>
      </Stack>
      <Stack direction="row" gap={rowGap} width="100%">
        <Stack flex={1} minWidth={0}>
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <FormInput
                label={STRINGS.player.mutatePlayer.contact.phoneNumber}
                value={value}
                required
                maxLength={MAX_PHONE_LENGTH}
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                keyboardType="phone-pad"
                onBlur={onBlur}
                onChangeText={phoneNumber => onChange(phoneNumber)}
                placeholder={''}
                phoneNumber
                onCountryCodeChange={countryCode => setValue('countryCode', countryCode)}
              />
            )}
          />
        </Stack>
        <Stack flex={1} minWidth={0}>
          <Controller
            name="altPhoneNumber"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <FormInput
                label={STRINGS.player.mutatePlayer.contact.altPhoneNumber}
                required={false}
                value={value}
                maxLength={MAX_PHONE_LENGTH}
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                keyboardType="phone-pad"
                onBlur={onBlur}
                onChangeText={altPhoneNumber => onChange(altPhoneNumber)}
                placeholder={''}
                phoneNumber
                onCountryCodeChange={countryCode => setValue('altCountryCode', countryCode)}
              />
            )}
          />
        </Stack>
        <Stack flex={1} minWidth={0}>
          <Controller
            name="email"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <FormInput
                label={STRINGS.player.mutatePlayer.contact.email}
                value={value}
                required
                trim
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                maxLength={MAX_EMAIL_LENGTH}
                onBlur={onBlur}
                keyboardType="email-address"
                autoCapitalize="none"
                autoComplete="email"
                textContentType="emailAddress"
                onChangeText={email => onChange(email)}
                placeholder={STRINGS.player.mutatePlayer.contact.email}
              />
            )}
          />
        </Stack>
      </Stack>
      <Stack direction="row" gap={rowGap} width="100%" align="flex-start">
        <Stack flex={1} minWidth={0}>
          <Controller
            name="country"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CountryRegionField
                {...addressFieldProps}
                label={STRINGS.player.mutatePlayer.contact.country}
                required={false}
                value={value}
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                onValueChange={nextCountry => {
                  onChange(nextCountry);
                  setValue('state', '', { shouldDirty: true });
                }}
              />
            )}
          />
        </Stack>
        <Stack flex={1} minWidth={0}>
          <Controller
            name="state"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <StateProvinceField
                {...addressFieldProps}
                label={STRINGS.player.mutatePlayer.contact.state}
                required={false}
                country={country}
                value={value}
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                onValueChange={onChange}
              />
            )}
          />
        </Stack>
      </Stack>
      <Stack direction="row" gap={rowGap} width="100%" align="flex-start">
        <Stack flexGrow={1} flexShrink={1} flexBasis={0} minWidth={0}>
          <Controller
            name="city"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <FormInput
                label={STRINGS.player.mutatePlayer.contact.city}
                required={false}
                value={value}
                trim
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                maxLength={MAX_CITY_LENGTH}
                onBlur={onBlur}
                onChangeText={city => onChange(city)}
                placeholder={STRINGS.player.mutatePlayer.contact.city}
              />
            )}
          />
        </Stack>
        <Stack flexGrow={1} flexShrink={1} flexBasis={0} minWidth={0}>
          <Controller
            name="zipCode"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <FormInput
                label={STRINGS.player.mutatePlayer.contact.zipCode}
                required={false}
                value={value}
                trim
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                maxLength={MAX_ZIP_LENGTH}
                keyboardType="numeric"
                onBlur={onBlur}
                onChangeText={zipCode => onChange(zipCode.replace(/[^0-9]/g, ''))}
                placeholder={STRINGS.player.mutatePlayer.contact.zipCode}
              />
            )}
          />
        </Stack>
      </Stack>
    </>
  );
};
